import { AnalysisSettings, LoadSettings } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

import {
  pushDownAnalysisSettings,
  pushDownLoadSettings,
  saveComponentAnalysisSettings,
  saveComponentLoadSettings,
  saveDefaultAnalysisSettingsInProject,
  saveDefaultLoadSettingsInProject,
} from '../../store/actions/settingsActions';
import { findTrussByName, requireProject, requireString, waitForAppToSettle } from './helpers';
import { getSettingChoices, SETTING_CHOICE_NOTES, SETTING_CHOICES } from './settingChoices';
import { defineTool, ToolError } from './types';

type SettingsKind = 'load' | 'analysis';

const KIND_SCHEMA = {
  type: 'string',
  enum: ['load', 'analysis'],
  description: 'Load settings (dead, live, snow, wind) or analysis settings.',
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Applies changes to settings, refusing keys the settings don't have, values of a different type, and choices not
 * among `choices` (by path), so a typo can't add a setting the server would ignore. Returns the new settings and the
 * path of each value that changed.
 */
export function applySettingChanges<T extends object>(
  current: T,
  changes: unknown,
  choices: Readonly<Record<string, readonly string[]>> = {},
  path = '',
): { settings: T; changed: string[] } {
  if (!isPlainObject(changes)) throw new ToolError(`The changes${path ? ` to ${path}` : ''} must be an object.`);

  const settings: Record<string, unknown> = { ...(current as Record<string, unknown>) };
  const changed: string[] = [];

  for (const [key, value] of Object.entries(changes)) {
    const keyPath = path ? `${path}.${key}` : key;
    if (!(key in settings))
      throw new ToolError(`There is no setting "${keyPath}". Read the settings with get_project_settings first.`);

    const currentValue = settings[key];
    if (isPlainObject(currentValue) && isPlainObject(value)) {
      const nested = applySettingChanges(currentValue, value, choices, keyPath);
      settings[key] = nested.settings;
      changed.push(...nested.changed);
      continue;
    }

    if (currentValue !== null && value !== null && typeof currentValue !== typeof value)
      throw new ToolError(`"${keyPath}" must be a ${Array.isArray(currentValue) ? 'list' : typeof currentValue}.`);

    const allowed = choices[keyPath];
    if (allowed != null && !allowed.includes(value as string))
      throw new ToolError(`"${keyPath}" must be one of ${allowed.join(', ')}, not ${JSON.stringify(value)}.`);

    if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
      settings[key] = value;
      changed.push(keyPath);
    }
  }

  return { settings: settings as T, changed };
}

/**
 * Saves settings, turning the server's refusal of a value it doesn't accept, such as a misspelled choice, into an
 * error that names what was changed.
 */
async function saveOrExplain(changed: readonly string[], save: () => Promise<unknown>) {
  try {
    await save();
  } catch (error) {
    if (error instanceof ToolError) throw error;
    throw new ToolError(
      `The app couldn’t save ${changed.join(', ')}. A value may not be one the setting accepts: a choice must be ` +
        'one of the allowedValues get_project_settings or get_truss_settings lists for it.',
    );
  }
}

function readSettings(kind: SettingsKind, projectGuid: string): Promise<LoadSettings | AnalysisSettings> {
  return kind === 'load'
    ? designService.settings.getModelLoadSettings(projectGuid)
    : designService.settings.getModelAnalysisSettings(projectGuid);
}

/** The settings with the values each choice in them accepts, as the get tools return them. */
async function readSettingsWithChoices(kind: SettingsKind, guid: string) {
  const settings = await readSettings(kind, guid);
  return { settings, allowedValues: getSettingChoices(kind, settings), notes: SETTING_CHOICE_NOTES[kind] };
}

function requireKind(kind: unknown): SettingsKind {
  if (kind !== 'load' && kind !== 'analysis') throw new ToolError('"kind" must be load or analysis.');
  return kind;
}

function readPath(settings: unknown, path: string) {
  return path.split('.').reduce<unknown>((value, key) => (isPlainObject(value) ? value[key] : undefined), settings);
}

/**
 * Reads the settings back after a save, so the result shows what the app kept, not just what was asked for.
 */
async function readBackChanges(
  kind: SettingsKind,
  guid: string,
  requested: object,
  changed: readonly string[],
): Promise<{ saved: Record<string, unknown>; notKept: string[] }> {
  const after = await readSettings(kind, guid);
  const saved = Object.fromEntries(changed.map((path) => [path, readPath(after, path)]));
  const notKept = changed.filter(
    (path) => JSON.stringify(readPath(after, path)) !== JSON.stringify(readPath(requested, path)),
  );
  return { saved, notKept };
}

export const settingsTools = [
  defineTool<{ kind: SettingsKind }>({
    name: 'get_project_settings',
    description:
      'Reads the project’s default load or analysis settings, which new trusses get and existing ones can be given, ' +
      'with allowedValues: the exact values each choice accepts, such as terrainExposureCategory ' +
      '"C_ScatteredObstructions".',
    inputSchema: { type: 'object', properties: { kind: KIND_SCHEMA }, required: ['kind'] },
    changesProject: false,
    async execute(input, { getState }) {
      const projectGuid = requireProject(getState());
      return { summary: await readSettingsWithChoices(requireKind(input.kind), projectGuid) };
    },
  }),

  defineTool<{ kind: SettingsKind; changes: Record<string, unknown>; applyToExistingTrusses?: boolean }>({
    name: 'change_project_settings',
    description:
      'Changes the project’s default load or analysis settings, as the settings panel’s Save does. Give only the ' +
      'settings to change, nested as get_project_settings returns them, each choice one of its allowedValues. ' +
      'applyToExistingTrusses also gives them to ' +
      'every truss in the project, which drops their analysis.',
    inputSchema: {
      type: 'object',
      properties: {
        kind: KIND_SCHEMA,
        changes: { type: 'object', description: 'For example {"windSettings": {"windSpeed": 130}}.' },
        applyToExistingTrusses: { type: 'boolean', description: 'Defaults to false.' },
      },
      required: ['kind', 'changes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const projectGuid = requireProject(getState());
      const kind = requireKind(input.kind);
      const current = await readSettings(kind, projectGuid);
      const { settings, changed } = applySettingChanges(current, input.changes, SETTING_CHOICES[kind]);
      if (changed.length === 0) return { summary: { changed: [] } };

      // The project is the top of its layout hierarchy, so its guid is also its model guid.
      if (kind === 'load') {
        const loadSettings = settings as LoadSettings;
        await saveOrExplain(changed, () =>
          dispatch(
            input.applyToExistingTrusses
              ? pushDownLoadSettings(projectGuid, projectGuid, loadSettings)
              : saveDefaultLoadSettingsInProject(projectGuid, projectGuid, loadSettings, true),
          ),
        );
      } else {
        const analysisSettings = settings as AnalysisSettings;
        await saveOrExplain(changed, () =>
          dispatch(
            input.applyToExistingTrusses
              ? pushDownAnalysisSettings(projectGuid, projectGuid, analysisSettings)
              : saveDefaultAnalysisSettingsInProject(projectGuid, projectGuid, analysisSettings, true),
          ),
        );
      }
      await waitForAppToSettle(getState);

      const { saved, notKept } = await readBackChanges(kind, projectGuid, settings, changed);
      return {
        summary: {
          saved,
          ...(notKept.length > 0 ? { notKept } : {}),
          appliedToExistingTrusses: input.applyToExistingTrusses ?? false,
        },
      };
    },
  }),

  defineTool<{ truss: string; kind: SettingsKind }>({
    name: 'get_truss_settings',
    description:
      'Reads one truss’s own load or analysis settings, which may differ from the project’s defaults, with ' +
      'allowedValues: the exact values each choice accepts.',
    inputSchema: {
      type: 'object',
      properties: { truss: { type: 'string', description: 'The truss’s name.' }, kind: KIND_SCHEMA },
      required: ['truss', 'kind'],
    },
    changesProject: false,
    async execute(input, { getState }) {
      const componentDesign = findTrussByName(getState(), requireString(input, 'truss'));
      return { summary: await readSettingsWithChoices(requireKind(input.kind), componentDesign.guid) };
    },
  }),

  defineTool<{ trusses: string[]; kind: SettingsKind; changes: Record<string, unknown> }>({
    name: 'change_truss_settings',
    description:
      'Changes the load or analysis settings of particular trusses, as Truss Settings > Save for this truss does, ' +
      'for example a girder’s loading or a deflection limit. Give only the settings to change, nested as ' +
      'get_truss_settings returns them, each choice one of its allowedValues. It drops their analysis, so run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        trusses: { type: 'array', items: { type: 'string' } },
        kind: KIND_SCHEMA,
        changes: { type: 'object' },
      },
      required: ['trusses', 'kind', 'changes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const kind = requireKind(input.kind);
      if (!Array.isArray(input.trusses) || input.trusses.length === 0) throw new ToolError('Name at least one truss.');
      const componentDesigns = input.trusses.map((name) => findTrussByName(getState(), name));

      const results = [];
      for (const componentDesign of componentDesigns) {
        const { settings, changed } = applySettingChanges(
          await readSettings(kind, componentDesign.guid),
          input.changes,
          SETTING_CHOICES[kind],
        );
        if (changed.length > 0)
          await saveOrExplain(changed, () =>
            dispatch(
              kind === 'load'
                ? saveComponentLoadSettings(componentDesign.guid, settings as LoadSettings)
                : saveComponentAnalysisSettings(componentDesign.guid, settings as AnalysisSettings),
            ),
          );
        results.push({ componentDesign, settings, changed });
      }
      await waitForAppToSettle(getState);
      return {
        summary: {
          results: await Promise.all(
            results.map(async ({ componentDesign, settings, changed }) => {
              const { saved, notKept } = await readBackChanges(kind, componentDesign.guid, settings, changed);
              return { truss: componentDesign.component.name, saved, ...(notKept.length > 0 ? { notKept } : {}) };
            }),
          ),
        },
      };
    },
  }),
];
